import { defineStore } from 'pinia';
import { ref } from 'vue';
import { customizationApi } from '../utils/api';

interface Customization {
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  headerBgColor: string;
  sidebarBgColor: string;
  textPrimaryColor: string;
  textSecondaryColor: string;
  headerFont: string;
  bodyFont: string;
  primaryLogoUrl: string | null;
  iconLogoUrl: string | null;
  faviconUrl: string | null;
  schoolName: string;
  slogan: string | null;
}

const defaultCustomization: Customization = {
  primaryColor: '#3B82F6',
  secondaryColor: '#10B981',
  accentColor: '#5A67D8',
  headerBgColor: '#1F2937',
  sidebarBgColor: '#111827',
  textPrimaryColor: '#111827',
  textSecondaryColor: '#6B7280',
  headerFont: 'Inter',
  bodyFont: 'Inter',
  primaryLogoUrl: null,
  iconLogoUrl: null,
  faviconUrl: null,
  schoolName: 'School Portal',
  slogan: null,
};

export const useCustomizationStore = defineStore('customization', () => {
  const customization = ref<Customization>({ ...defaultCustomization });
  const isLoaded = ref(false);

  /**
   * Apply CSS variables to the document root
   */
  function applyColors() {
    const root = document.documentElement;
    const c = customization.value;

    // Primary color and variants
    root.style.setProperty('--color-primary', c.primaryColor);
    root.style.setProperty('--color-primary-50', adjustColor(c.primaryColor, 0.95));
    root.style.setProperty('--color-primary-100', adjustColor(c.primaryColor, 0.9));
    root.style.setProperty('--color-primary-500', c.primaryColor);
    root.style.setProperty('--color-primary-600', adjustColor(c.primaryColor, -0.1));
    root.style.setProperty('--color-primary-700', adjustColor(c.primaryColor, -0.2));

    // Secondary color and variants
    root.style.setProperty('--color-secondary', c.secondaryColor);
    root.style.setProperty('--color-secondary-50', adjustColor(c.secondaryColor, 0.95));
    root.style.setProperty('--color-secondary-100', adjustColor(c.secondaryColor, 0.9));
    root.style.setProperty('--color-secondary-500', c.secondaryColor);
    root.style.setProperty('--color-secondary-600', adjustColor(c.secondaryColor, -0.1));
    root.style.setProperty('--color-secondary-700', adjustColor(c.secondaryColor, -0.2));

    // Accent color
    root.style.setProperty('--color-accent', c.accentColor);

    // Layout colors
    root.style.setProperty('--color-header-bg', c.headerBgColor);
    root.style.setProperty('--color-sidebar-bg', c.sidebarBgColor);
    root.style.setProperty('--color-text-primary', c.textPrimaryColor);
    root.style.setProperty('--color-text-secondary', c.textSecondaryColor);

    // Fonts
    root.style.setProperty('--font-header', `'${c.headerFont}', system-ui, sans-serif`);
    root.style.setProperty('--font-body', `'${c.bodyFont}', system-ui, sans-serif`);
    root.style.setProperty('--font-family', `'${c.bodyFont}', system-ui, sans-serif`);

    // Update favicon if available
    if (c.faviconUrl) {
      updateFavicon(c.faviconUrl);
    }
  }

  /**
   * Load customization from API
   */
  async function loadCustomization() {
    try {
      const data = await customizationApi.get();
      customization.value = {
        primaryColor: data.primaryColor || defaultCustomization.primaryColor,
        secondaryColor: data.secondaryColor || defaultCustomization.secondaryColor,
        accentColor: data.accentColor || defaultCustomization.accentColor,
        headerBgColor: data.headerBgColor || defaultCustomization.headerBgColor,
        sidebarBgColor: data.sidebarBgColor || defaultCustomization.sidebarBgColor,
        textPrimaryColor: data.textPrimaryColor || defaultCustomization.textPrimaryColor,
        textSecondaryColor: data.textSecondaryColor || defaultCustomization.textSecondaryColor,
        headerFont: data.headerFont || defaultCustomization.headerFont,
        bodyFont: data.bodyFont || defaultCustomization.bodyFont,
        primaryLogoUrl: data.primaryLogoUrl,
        iconLogoUrl: data.iconLogoUrl,
        faviconUrl: data.faviconUrl,
        schoolName: data.welcomeMessage || defaultCustomization.schoolName,
        slogan: data.slogan || null,
      };
      applyColors();
      isLoaded.value = true;
    } catch (err) {
      console.error('Failed to load customization:', err);
      // Apply defaults on error
      applyColors();
    }
  }

  /**
   * Update customization (after saving in settings)
   */
  function updateCustomization(data: Partial<Customization>) {
    customization.value = { ...customization.value, ...data };
    applyColors();
  }

  /**
   * Reset to defaults
   */
  function resetToDefaults() {
    customization.value = { ...defaultCustomization };
    applyColors();
  }

  return {
    customization,
    isLoaded,
    loadCustomization,
    updateCustomization,
    resetToDefaults,
    applyColors,
  };
});

/**
 * Adjust color brightness
 * factor > 0 lightens, factor < 0 darkens
 */
function adjustColor(hex: string, factor: number): string {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Adjust
  let newR: number, newG: number, newB: number;
  if (factor > 0) {
    // Lighten: mix with white
    newR = Math.round(r + (255 - r) * factor);
    newG = Math.round(g + (255 - g) * factor);
    newB = Math.round(b + (255 - b) * factor);
  } else {
    // Darken: reduce values
    const f = 1 + factor;
    newR = Math.round(r * f);
    newG = Math.round(g * f);
    newB = Math.round(b * f);
  }

  // Clamp and convert back to hex
  const toHex = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}

/**
 * Update the page favicon
 */
function updateFavicon(url: string) {
  let link: HTMLLinkElement | null = document.querySelector("link[rel~='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = url;
}
